import {Box, Clickable, Hide, Image, Space, Text, useDevice, VStack} from 'platform/foundation';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {VehicleImage} from '../../../components/VehicleImage/VehicleImage';
import {CatalogueType} from '../../../hooks/useCatalogue';
import i18n from '../../../i18n/i18n';
import {PriceReportType} from '../../../types/PriceReportType';
import {AvailableCarWithLogo} from '../types/available-car-with-logo';
import {getAdvertisementVehicleBackgroundColor} from '../utils/getAdvertisementVehicleBackgroundColor';
import {openAdLink} from '../utils/openAdLink';
import {ColumnHeader} from './ColumnHeader';
import {FeatureList} from './FeatureList';

interface VehicleColumnProps extends TestIdProps {
  vehicle: AvailableCarWithLogo;
  vehiclePosition: number | Nullish;
  priceReport: PriceReportType;
  catalogue: CatalogueType;
  isMyVehicle: boolean;
  vehicleFeatures?: string[];
  hasAnyVehicleDiscount: boolean;
}

export function VehicleColumn(props: VehicleColumnProps) {
  const device = useDevice();
  const isMobile = device === 'mobile';

  const isMyAd =
    isNotNil(props.priceReport.vehicle.adId) &&
    props.priceReport.vehicle.adId === props.vehicle.ad_id;

  const backgroundColor = getAdvertisementVehicleBackgroundColor({
    isMyVehicle: props.isMyVehicle,
    isMyAd,
    isPricingNeighbour: props.vehicle.isPricingNeighbour,
  });

  const handleClick = () => {
    if (!props.vehicle.is_sold && isNotNil(props.vehicle.url)) {
      openAdLink(props.vehicle.url, props.priceReport.market, i18n.language);
    }
  };

  return (
    <Box
      backgroundColor={backgroundColor}
      width={38}
      paddingTop={2}
      borderRadius="small"
      boxShadow={props.isMyVehicle ? 'elevation_3' : undefined}
      position={props.isMyVehicle ? 'sticky' : undefined}
      left={props.isMyVehicle ? 35 : undefined}
      right={props.isMyVehicle ? 0 : undefined}
      zIndex={props.isMyVehicle ? 'SIDEBAR' : undefined}
    >
      <Box borderRight="1px solid" borderBottom="1px solid" borderColor="general.separator">
        <VStack>
          {isNilOrEmpty(props.priceReport.vehicle.imageUrl) && isNotNil(props.vehicle.logoUrl) ? (
            <Box>
              <VStack justify="center" align="center" height={24}>
                <Image
                  src={props.vehicle.logoUrl}
                  width="auto"
                  maxHeight={10}
                  data-testid={suffixTestId('vehicleLogo', props)}
                />
                <Space vertical={3} />
                <Text size="xSmall" color="tertiary" alternative>
                  {i18n.t('page.advertisements.yourVehicle')}
                </Text>
              </VStack>
            </Box>
          ) : (
            <Clickable
              onClick={handleClick}
              isDisabled={!!props.vehicle.is_sold || props.isMyVehicle}
              data-testid={suffixTestId('vehicleImage', props)}
            >
              <VehicleImage
                priceReport={props.priceReport}
                vehicle={props.vehicle}
                isMyVehicle={props.isMyVehicle}
                maxHeight={24}
              />
            </Clickable>
          )}
          <Box padding={isMobile ? 2 : undefined}>
            <Hide onMobile>
              <ColumnHeader
                priceReport={props.priceReport}
                catalogue={props.catalogue}
                vehicle={props.vehicle}
                vehiclePosition={props.vehiclePosition}
                isMyVehicle={props.isMyVehicle}
                isMyAd={isMyAd}
                isPricingNeighbour={props.vehicle.isPricingNeighbour}
                hasAnyVehicleDiscount={props.hasAnyVehicleDiscount}
                data-testid={suffixTestId('columnHeader', props)}
              />
            </Hide>
            <FeatureList
              firstWhite
              values={props.vehicleFeatures}
              compareWith={props.vehicle.features ?? []}
              isBold={true}
              isRow={false}
              spacing={2}
              textColor="primary"
              isSortable
              isMyVehicle={props.isMyVehicle}
              isMyAd={isMyAd}
              isPricingNeighbour={props.vehicle.isPricingNeighbour}
              data-testid={suffixTestId('featureList', props)}
            />
          </Box>
        </VStack>
      </Box>
    </Box>
  );
}
